// FY27 Budget v2 — the 06-JUL revision to the 15-JUN approved budget (v1).
// Committee → Category → Expense-type (the board's decision unit). Each line
// carries FY24–FY26 history, the FY27 v1 baseline + FY27 v2, a supplier_detail
// backing, and a PO/Non-PO/Card spend drill-down. Plus per-committee FY26
// carryover, the projects schedule, capital schedule, OCA rollup, and a polished
// top hero (donut + trajectory). Fully theme-aware (light/dark).
import { useMemo, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useFy27V2 } from "../hooks/useFinance";
import type {
  Fy27V2Committee,
  Fy27V2Category,
  Fy27V2Line,
  Fy27V2Project,
} from "../hooks/useFinance";
import PageShell from "../components/shared/PageShell";
import PageHeader from "../components/shared/PageHeader";
import {
  usd0,
  usdC,
  COLORS,
  ACCENT,
  MUTED,
  TOOLTIP_PROPS,
} from "../components/finance/charts";
import { FetchErrorBanner } from "../components/finance/ui";

// numeric cells share one tabular-nums style; sizing is set per nesting level.
const NUM = "px-3 py-1.5 text-right tabular-nums";
const LBL = "px-3 py-1.5";

/** Exact-cents currency — open-PO carryover / ALL-IN must tie to the cent. */
const usd2 = (n: number) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** "why" marker — only renders when there's substantive text to show. */
function Why({ text }: { text?: string | null }) {
  if (!text || text.trim().length < 12) return null;
  return (
    <span
      title={text}
      className="ml-1.5 cursor-help text-[11px] text-theme-muted"
    >
      ⓘ
    </span>
  );
}

/** +/- delta in emerald/rose with a neutral em-dash at ~zero. */
function Delta({ v, className = "" }: { v: number; className?: string }) {
  if (Math.abs(v) < 1) return <span className="text-theme-muted">—</span>;
  const up = v > 0;
  return (
    <span
      className={`font-semibold ${className} ${
        up
          ? "text-emerald-600 dark:text-emerald-400"
          : "text-rose-600 dark:text-rose-400"
      }`}
    >
      {up ? "▲" : "▼"} {usd0(Math.abs(v))}
    </span>
  );
}

// ── Top hero: stat cards + a donut (v2 composition) + a 4-year trajectory area ──
function TopHero({
  data,
}: {
  data: NonNullable<ReturnType<typeof useFy27V2>["data"]>;
}) {
  const g = data.grand;
  const capTotal = g.capital;

  const pie = useMemo(
    () =>
      data.chart
        .map((r) => ({ name: r.committee, value: r.fy27_v2 }))
        .filter((d) => d.value > 0)
        .sort((a, b) => b.value - a.value),
    [data.chart],
  );

  // 4-year trajectory: FY24/25/26 actuals (sum of category subtotals) + FY27 v2.
  const trend = useMemo(() => {
    let fy24 = 0,
      fy25 = 0,
      fy26 = 0;
    for (const c of data.committees)
      for (const cat of c.categories) {
        fy24 += cat.sub.fy24;
        fy25 += cat.sub.fy25;
        fy26 += cat.sub.fy26;
      }
    return [
      { fy: "FY24", total: Math.round(fy24) },
      { fy: "FY25", total: Math.round(fy25) },
      { fy: "FY26", total: Math.round(fy26) },
      { fy: "FY27 v2", total: Math.round(g.recurring) },
    ];
  }, [data.committees, g.recurring]);

  return (
    <div className="my-5 grid grid-cols-1 gap-4 lg:grid-cols-12">
      {/* stat cards */}
      <div className="grid grid-cols-2 gap-3 lg:col-span-4">
        <Stat label="FY27 v2 recurring" value={usd0(g.recurring)} strong />
        <Stat label="v1 (15-JUN)" value={usd0(g.v1_recurring)} />
        <Stat
          label="Δ v1 → v2"
          value={`${g.delta >= 0 ? "+" : ""}${usd0(g.delta)}`}
          accent={g.delta >= 0 ? "up" : "down"}
        />
        <Stat label="Capital / one-time" value={usd0(capTotal)} />
      </div>

      {/* donut: v2 by committee */}
      <div className="rounded-xl border border-theme-subtle bg-theme-surface p-4 lg:col-span-4">
        <div className="mb-1 text-xs font-semibold text-theme-muted">
          FY27 v2 recurring by committee
        </div>
        <div className="flex items-center gap-3">
          <div className="h-[150px] w-[150px] shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pie}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={42}
                  outerRadius={70}
                  paddingAngle={1.5}
                  stroke="none"
                >
                  {pie.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  {...TOOLTIP_PROPS}
                  formatter={(v) => usd0(Number(v))}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="min-w-0 flex-1 space-y-0.5 text-xs">
            {pie.map((d, i) => (
              <li key={d.name} className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                  style={{ background: COLORS[i % COLORS.length] }}
                />
                <span className="min-w-0 flex-1 truncate text-theme-secondary">
                  {d.name}
                </span>
                <span className="shrink-0 tabular-nums text-theme-muted">
                  {usd0(d.value)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* trajectory area */}
      <div className="rounded-xl border border-theme-subtle bg-theme-surface p-4 lg:col-span-4">
        <div className="mb-2 text-xs font-semibold text-theme-muted">
          Budget trajectory (FY24 → FY27)
        </div>
        <div className="h-[150px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={trend}
              margin={{ top: 4, right: 6, bottom: 0, left: 0 }}
            >
              <defs>
                <linearGradient id="v2grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={ACCENT} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={ACCENT} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="fy"
                tick={{ fontSize: 11, fill: MUTED }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tickFormatter={(v) => usdC(v)}
                tick={{ fontSize: 10, fill: MUTED }}
                axisLine={false}
                tickLine={false}
                width={42}
              />
              <Tooltip {...TOOLTIP_PROPS} formatter={(v) => usd0(Number(v))} />
              <Area
                type="monotone"
                dataKey="total"
                stroke={ACCENT}
                strokeWidth={2.5}
                fill="url(#v2grad)"
                dot={{ r: 3, fill: ACCENT, strokeWidth: 0 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  strong,
  accent,
}: {
  label: string;
  value: string;
  strong?: boolean;
  accent?: "up" | "down";
}) {
  const valCls = accent
    ? accent === "up"
      ? "text-emerald-600 dark:text-emerald-400"
      : "text-rose-600 dark:text-rose-400"
    : strong
      ? "text-white"
      : "text-theme-primary";
  return (
    <div
      className={`rounded-xl px-4 py-3 ${
        strong
          ? "bg-[#0E403A] text-white"
          : "border border-theme-subtle bg-theme-surface"
      }`}
    >
      <div
        className={`text-[11px] ${strong ? "text-white/75" : "text-theme-muted"}`}
      >
        {label}
      </div>
      <div className={`text-2xl font-bold ${valCls}`}>{value}</div>
    </div>
  );
}

// ── Per-line spend drill-down (PO / Non-PO / Card), grouped by channel ────────
function SpendDrill({ spend }: { spend: Fy27V2Line["spend"] }) {
  const groups = useMemo(() => {
    const m = new Map<string, Fy27V2Line["spend"]>();
    for (const s of spend) {
      if (!m.has(s.channel)) m.set(s.channel, []);
      m.get(s.channel)!.push(s);
    }
    return [...m.entries()];
  }, [spend]);
  if (spend.length === 0)
    return (
      <div className="px-10 py-2 text-[11px] italic text-theme-muted">
        No itemized spend on record for this line.
      </div>
    );
  return (
    <div className="bg-theme-overlay/40 px-10 py-2">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-theme-muted">
            <th className="py-1 text-left font-medium">Channel / item</th>
            <th className="py-1 text-right font-medium">FY25</th>
            <th className="py-1 text-right font-medium">FY26</th>
          </tr>
        </thead>
        <tbody>
          {groups.map(([channel, items]) => (
            <Channel key={channel} channel={channel} items={items} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Channel({
  channel,
  items,
}: {
  channel: string;
  items: Fy27V2Line["spend"];
}) {
  const fy25 = items.reduce((s, i) => s + i.fy25, 0);
  const fy26 = items.reduce((s, i) => s + i.fy26, 0);
  return (
    <>
      <tr>
        <td className="pt-1.5 font-semibold text-theme-secondary">{channel}</td>
        <td className="pt-1.5 text-right font-semibold tabular-nums text-theme-secondary">
          {usd0(fy25)}
        </td>
        <td className="pt-1.5 text-right font-semibold tabular-nums text-theme-secondary">
          {usd0(fy26)}
        </td>
      </tr>
      {items.map((i, idx) => (
        <tr key={`${channel}-${idx}`}>
          <td className="py-0.5 pl-4 text-theme-muted">{i.label}</td>
          <td className="py-0.5 text-right tabular-nums text-theme-muted">
            {usd0(i.fy25)}
          </td>
          <td className="py-0.5 text-right tabular-nums text-theme-muted">
            {usd0(i.fy26)}
          </td>
        </tr>
      ))}
    </>
  );
}

// ── One expense-type line (expandable to its spend drill-down) — SMALLEST text ─
function LineRow({ l }: { l: Fy27V2Line }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="text-sm hover:bg-theme-overlay/30">
        <td className={`${LBL} pl-7`}>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="mr-1.5 inline-flex w-3 text-[10px] text-theme-muted hover:text-theme-primary"
            aria-label={open ? "Collapse spend" : "Expand spend"}
          >
            {open ? "▾" : "▸"}
          </button>
          <span className="text-theme-primary">{l.expense_type}</span>
          <Why text={l.reasoning} />
          {l.needs_director_confirm === 1 && (
            <span
              title="Needs director confirmation"
              className="ml-1.5 text-amber-600 dark:text-amber-400"
            >
              ⚑
            </span>
          )}
          {l.supplier_detail && (
            <div className="pl-4 text-[11px] text-theme-muted">
              {l.supplier_detail}
            </div>
          )}
        </td>
        <td className={`${NUM} text-theme-secondary`}>{usd0(l.fy24_actual)}</td>
        <td className={`${NUM} text-theme-secondary`}>{usd0(l.fy25_actual)}</td>
        <td className={`${NUM} text-theme-secondary`}>{usd0(l.fy26_actual)}</td>
        <td className={`${NUM} text-theme-muted`}>
          {l.fy27_v1 ? usd0(l.fy27_v1) : "—"}
        </td>
        <td className={`${NUM} font-semibold text-theme-primary`}>
          {usd0(l.fy27_v2)}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6} className="p-0">
            <SpendDrill spend={l.spend} />
          </td>
        </tr>
      )}
    </>
  );
}

// ── One category (collapsible) — subtotal lives ON the header row; MEDIUM text ─
function CategoryBlock({ cat }: { cat: Fy27V2Category }) {
  const [open, setOpen] = useState(true);
  const subDelta = cat.sub.fy27_v2 - cat.sub.fy27_v1;
  return (
    <>
      <tr className="bg-theme-subtle/70 text-base font-semibold">
        <td className={LBL}>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="text-theme-secondary hover:text-theme-primary"
          >
            {open ? "▾" : "▸"} {cat.category}
          </button>
          {Math.abs(subDelta) >= 1 && (
            <span className="ml-2 text-[11px] font-normal">
              <Delta v={subDelta} />
            </span>
          )}
        </td>
        <td className={`${NUM} text-theme-secondary`}>{usd0(cat.sub.fy24)}</td>
        <td className={`${NUM} text-theme-secondary`}>{usd0(cat.sub.fy25)}</td>
        <td className={`${NUM} text-theme-secondary`}>{usd0(cat.sub.fy26)}</td>
        <td className={`${NUM} text-theme-muted`}>
          {cat.sub.fy27_v1 ? usd0(cat.sub.fy27_v1) : "—"}
        </td>
        <td className={`${NUM} text-theme-primary`}>{usd0(cat.sub.fy27_v2)}</td>
      </tr>
      {open && cat.lines.map((l) => <LineRow key={l.id} l={l} />)}
    </>
  );
}

// ── Layer 2: the committee's projects (funded / in-recurring / not funded) ────
// Funded = additive one-time money. In-recurring = already inside the lines
// above (tagged, never re-added). Not funded = collapsed note.
function CommitteeProjects({ c }: { c: Fy27V2Committee }) {
  if (
    c.projects_funded.length === 0 &&
    c.projects_in_recurring.length === 0 &&
    c.projects_not_funded.length === 0
  )
    return null;
  return (
    <div className="border-t border-theme-subtle px-3.5 py-2">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-theme-secondary">
          Funded projects (one-time — additive)
        </span>
        <span className="text-xs font-semibold tabular-nums text-theme-primary">
          {usd0(c.projects_funded_total)}
        </span>
      </div>
      {c.projects_funded.length === 0 && (
        <p className="text-[12px] italic text-theme-muted">
          No one-time funded projects for this committee.
        </p>
      )}
      <ul className="space-y-0.5">
        {c.projects_funded.map((p) => (
          <li
            key={`${p.ref}-${p.name}`}
            className="flex items-center justify-between gap-2 text-[12px] text-theme-secondary"
          >
            <span className="min-w-0">
              {p.ref ? `${p.ref} ` : ""}
              {p.name}
              <span className="ml-1.5 text-[11px] text-theme-muted">
                {p.funding_source || "—"} · {p.status || "—"}
              </span>
              <Why text={p.source_note} />
            </span>
            <span className="shrink-0 tabular-nums text-theme-primary">
              {usd0(p.amount)}
            </span>
          </li>
        ))}
        {c.projects_in_recurring.map((p) => (
          <li
            key={`ir-${p.ref}-${p.name}`}
            className="flex items-center justify-between gap-2 text-[12px] text-theme-muted"
          >
            <span className="min-w-0">
              {p.ref ? `${p.ref} ` : ""}
              {p.name}
              <span className="ml-1.5 rounded-full bg-dcvfd-accent/15 px-2 py-0.5 text-[10px] font-medium text-dcvfd-accent">
                in recurring above — not re-added
              </span>
            </span>
            <span className="shrink-0 tabular-nums">{usd0(p.amount)}</span>
          </li>
        ))}
      </ul>
      {c.projects_not_funded.length > 0 && (
        <details className="mt-1">
          <summary className="cursor-pointer text-[11px] text-theme-muted">
            Not funded ({c.projects_not_funded.length})
          </summary>
          <ul className="mt-0.5 space-y-0.5 pl-3">
            {c.projects_not_funded.map((p) => (
              <li
                key={`nf-${p.ref}-${p.name}`}
                className="text-[12px] text-theme-muted"
              >
                {p.ref ? `${p.ref} ` : ""}
                {p.name} {usd0(p.amount)} — {p.status}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

// ── Layer 3: FY26 open-PO carryover (encumbrances) — the REAL carryover ───────
function PoCarryoverBlock({ c, label }: { c: Fy27V2Committee; label: string }) {
  const [open, setOpen] = useState(false);
  if (c.po_carryover.count === 0) return null;
  return (
    <div className="border-t border-theme-subtle px-3.5 py-2">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="min-w-0 text-xs font-semibold uppercase tracking-wider text-theme-secondary">
          {label}
        </span>
        <span className="shrink-0 text-xs font-semibold tabular-nums text-theme-primary">
          {usd0(c.po_carryover.total)}
        </span>
      </div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-[11px] text-theme-muted hover:text-theme-primary"
      >
        {open ? "▾" : "▸"} {c.po_carryover.count} open PO
        {c.po_carryover.count === 1 ? "" : "s"}
      </button>
      {open && (
        <ul className="mt-1 space-y-0.5">
          {c.po_carryover.pos.map((po) => (
            <li
              key={po.po_number}
              className="flex items-center justify-between gap-2 text-[12px] text-theme-secondary"
            >
              <span className="min-w-0 truncate">
                <span className="font-mono text-theme-muted">
                  {po.po_number}
                </span>{" "}
                — {po.supplier || "(supplier not in register)"}
              </span>
              <span className="shrink-0 tabular-nums text-theme-muted">
                {usd2(po.remaining)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── FY26 arrears exposure — informational sub-line ONLY, never additive ───────
function ArrearsBlock({ c }: { c: Fy27V2Committee }) {
  if (c.arrears.length === 0) return null;
  return (
    <div className="border-t border-theme-subtle bg-amber-50/60 px-3.5 py-2 dark:bg-amber-900/10">
      <div className="mb-1 text-xs font-semibold text-amber-700 dark:text-amber-300">
        {c.arrears_note}
      </div>
      <ul className="space-y-0.5">
        {c.arrears.map((a, i) => (
          <li
            key={i}
            className="flex items-center justify-between text-[12px] text-theme-secondary"
          >
            <span>
              {a.label}
              <Why text={a.basis} />
            </span>
            <span className="tabular-nums text-theme-muted">
              {usd0(a.amount)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── G-3: the labeled 3-layer ALL-IN footer — layers never blended unlabeled ───
function AllInFooter({ c }: { c: Fy27V2Committee }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-theme-subtle bg-theme-overlay px-3.5 py-2.5">
      <span className="text-[12px] text-theme-secondary">
        Recurring v2 <b className="tabular-nums">{usd0(c.recurring)}</b>
        {" + "}Funded projects{" "}
        <b className="tabular-nums">{usd0(c.projects_funded_total)}</b>
        {" + "}Open-PO carryover{" "}
        <b className="tabular-nums">{usd2(c.po_carryover.total)}</b>
      </span>
      <span className="text-sm font-bold text-theme-primary">
        ALL-IN <span className="tabular-nums">{usd2(c.all_in)}</span>
      </span>
    </div>
  );
}

// ── One committee (collapsible) — LARGEST text in the nesting ─────────────────
function CommitteeBlock({
  c,
  poCarryoverLabel,
}: {
  c: Fy27V2Committee;
  poCarryoverLabel: string;
}) {
  const [open, setOpen] = useState(true);
  const tot = useMemo(() => {
    let fy24 = 0,
      fy25 = 0,
      fy26 = 0;
    for (const cat of c.categories) {
      fy24 += cat.sub.fy24;
      fy25 += cat.sub.fy25;
      fy26 += cat.sub.fy26;
    }
    return { fy24, fy25, fy26 };
  }, [c.categories]);
  return (
    <div className="mb-4 overflow-hidden rounded-xl border border-theme-subtle">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 bg-theme-overlay px-4 py-3 text-left"
      >
        <span className="text-lg font-bold text-dcvfd-accent">
          {open ? "▾" : "▸"} {c.committee}
          {c.needs_confirm > 0 && (
            <span className="ml-2.5 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
              {c.needs_confirm} need director confirm
            </span>
          )}
        </span>
        <span className="text-sm text-theme-secondary">
          {c.is_unassigned ? (
            <span className="text-theme-muted">
              POs / projects without a committee mapping
            </span>
          ) : (
            <>
              v1 {usd0(c.v1_fy27)} →{" "}
              <b className="text-theme-primary">{usd0(c.recurring)}</b>
              &nbsp; <Delta v={c.delta_v1} />
            </>
          )}
          <span className="ml-3 whitespace-nowrap">
            ALL-IN{" "}
            <b className="tabular-nums text-theme-primary">{usd2(c.all_in)}</b>
          </span>
        </span>
      </button>
      {open && (
        <>
          {c.categories.length > 0 && (
            <table className="w-full border-collapse bg-theme-surface">
              <thead>
                <tr className="text-theme-muted">
                  <th
                    className={`${LBL} text-left text-xs font-semibold uppercase tracking-wider`}
                  >
                    Category / Expense type
                  </th>
                  <th
                    className={`${NUM} text-xs font-semibold uppercase tracking-wider`}
                  >
                    FY24
                  </th>
                  <th
                    className={`${NUM} text-xs font-semibold uppercase tracking-wider`}
                  >
                    FY25
                  </th>
                  <th
                    className={`${NUM} text-xs font-semibold uppercase tracking-wider`}
                  >
                    FY26
                  </th>
                  <th
                    className={`${NUM} text-xs font-semibold uppercase tracking-wider`}
                  >
                    FY27 v1
                  </th>
                  <th
                    className={`${NUM} text-xs font-semibold uppercase tracking-wider`}
                  >
                    FY27 v2
                  </th>
                </tr>
              </thead>
              <tbody>
                {c.categories.map((cat) => (
                  <CategoryBlock key={cat.category} cat={cat} />
                ))}
                {/* committee total */}
                <tr className="bg-theme-overlay text-base font-bold">
                  <td className={`${LBL} text-theme-primary`}>
                    {c.committee} total (recurring)
                  </td>
                  <td className={`${NUM} text-theme-secondary`}>
                    {usd0(tot.fy24)}
                  </td>
                  <td className={`${NUM} text-theme-secondary`}>
                    {usd0(tot.fy25)}
                  </td>
                  <td className={`${NUM} text-theme-secondary`}>
                    {usd0(tot.fy26)}
                  </td>
                  <td className={`${NUM} text-theme-muted`}>
                    {usd0(c.v1_fy27)}
                  </td>
                  <td className={`${NUM} text-theme-primary`}>
                    {usd0(c.recurring)}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
          {/* The three labeled layers (G-1/G-2) + the ALL-IN sum (G-3). */}
          <CommitteeProjects c={c} />
          <PoCarryoverBlock c={c} label={poCarryoverLabel} />
          <ArrearsBlock c={c} />
          <AllInFooter c={c} />
        </>
      )}
    </div>
  );
}

// ── Projects schedule, grouped by funding source ──────────────────────────────
const FUNDING_ORDER = [
  "Fund balance",
  "Operating levy",
  "Insurance",
  "Rejected",
  "Deferred",
];
function fundingRank(s: string | null): number {
  const i = FUNDING_ORDER.indexOf(s || "");
  return i === -1 ? FUNDING_ORDER.length : i;
}

function ProjectsSchedule({ projects }: { projects: Fy27V2Project[] }) {
  const grouped = useMemo(() => {
    const m = new Map<string, Fy27V2Project[]>();
    for (const p of projects) {
      const k = p.funding_source || "(unassigned)";
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(p);
    }
    return [...m.entries()].sort(
      (a, b) => fundingRank(a[0]) - fundingRank(b[0]),
    );
  }, [projects]);
  if (projects.length === 0) return null;
  return (
    <div className="mt-6">
      <h3 className="mb-2 text-base font-semibold text-dcvfd-accent">
        Projects schedule (one-time / capital — by funding source)
      </h3>
      <div className="space-y-4">
        {grouped.map(([source, rows]) => {
          const total = rows.reduce((s, p) => s + p.amount, 0);
          return (
            <div
              key={source}
              className="overflow-hidden rounded-xl border border-theme-subtle bg-theme-surface"
            >
              <div className="flex items-center justify-between bg-theme-overlay px-4 py-2">
                <span className="text-sm font-semibold text-theme-primary">
                  {source}
                </span>
                <span className="text-xs font-semibold tabular-nums text-theme-secondary">
                  {usd0(total)}
                </span>
              </div>
              <table className="w-full border-collapse text-sm">
                <tbody>
                  {rows.map((p) => (
                    <tr
                      key={`${source}-${p.ref}-${p.name}`}
                      className="hover:bg-theme-overlay/30"
                    >
                      <td className={`${LBL} font-mono text-theme-muted`}>
                        {p.ref}
                      </td>
                      <td className={LBL}>
                        <span className="text-theme-primary">{p.name}</span>
                        {p.affects_operating === 1 && (
                          <span
                            title="Affects the operating levy — already counted in FY27 recurring"
                            className="ml-2 rounded-full bg-dcvfd-accent/15 px-2 py-0.5 text-[10px] font-medium text-dcvfd-accent"
                          >
                            in recurring
                          </span>
                        )}
                        {p.committee && (
                          <span className="ml-2 text-[11px] text-theme-muted">
                            {p.committee}
                          </span>
                        )}
                        <Why text={p.source_note} />
                      </td>
                      <td className={`${LBL} text-theme-secondary`}>
                        {p.status || "—"}
                      </td>
                      <td className={`${NUM} font-semibold text-theme-primary`}>
                        {usd0(p.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function FinanceFY27V2() {
  const { data, error, isLoading } = useFy27V2();
  const [showOca, setShowOca] = useState(false);
  const subsidyNote = data?.meta?.subsidy_decision;

  return (
    <PageShell>
      <PageHeader
        title="FY27 Budget v2"
        subtitle="The 06-JUL revision to the 15-JUN approved budget (v1). Committee → Category → Expense-type. The board reviews the v1→v2 delta. Formal adoption of v2 is still pending."
      />
      {error && <FetchErrorBanner error={String(error)} />}
      {isLoading && <p className="text-theme-secondary">Loading v2…</p>}
      {data && data.grand && (
        <>
          <TopHero data={data} />
          {subsidyNote && (
            <p className="-mt-2 mb-4 text-xs text-theme-muted">
              Salary subsidy decision: {subsidyNote}. v0.5 (errors) → v1 (15-JUN
              approved, frozen) → v2 (this revision).
            </p>
          )}
          {/* B-C2: line-level v1 is a partial mapping, not the v1 control. */}
          <p className="mb-4 text-xs text-theme-muted">
            FY27 v1 note: line/category-level v1 detail maps only the v1 dollars
            carried into surviving v2 lines ($3,130,650 of the $3,435,100
            approved 15-JUN); lines added or restructured in v2 show “—”. The
            committee-level grand v1 total is the control.
          </p>

          {/* G-5 flag: source names that failed committee normalization. */}
          {data.unmapped_committees.length > 0 && (
            <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50/60 px-4 py-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-900/10 dark:text-amber-300">
              Unmapped committee name
              {data.unmapped_committees.length === 1 ? "" : "s"} shown under
              “Unassigned”:{" "}
              {data.unmapped_committees
                .map((u) => `${u.name} (${u.count})`)
                .join(", ")}
              . Fix the supplier→committee map / project tag.
            </div>
          )}

          {data.committees.map((c) => (
            <CommitteeBlock
              key={c.committee}
              c={c}
              poCarryoverLabel={data.labels.po_carryover}
            />
          ))}

          {/* G-3 grand: the three labeled layers — never blended unlabeled. */}
          <div className="mb-6 rounded-xl border border-theme-subtle bg-theme-overlay px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm text-theme-secondary">
                Recurring v2{" "}
                <b className="tabular-nums">{usd0(data.grand.recurring)}</b>
                {" + "}Funded projects{" "}
                <b className="tabular-nums">
                  {usd0(data.grand.projects_funded)}
                </b>
                {" + "}FY26→FY27 open-PO carryover{" "}
                <b className="tabular-nums">{usd2(data.grand.po_carryover)}</b>{" "}
                <span className="text-xs text-theme-muted">
                  ({data.grand.po_carryover_count} POs)
                </span>
              </span>
              <span className="text-base font-bold text-theme-primary">
                GRAND ALL-IN{" "}
                <span className="tabular-nums">{usd2(data.grand.all_in)}</span>
              </span>
            </div>
            <p className="mt-1 text-[11px] text-theme-muted">
              {data.labels.po_carryover}. {data.labels.po_carryover_footnote}{" "}
              Arrears exposure is informational and excluded from every sum.
            </p>
          </div>

          <ProjectsSchedule projects={data.projects} />

          {data.capital.length > 0 && (
            <div className="mt-6">
              <h3 className="mb-2 text-base font-semibold text-dcvfd-accent">
                Capital / One-Time schedule (separate from recurring)
              </h3>
              <table className="w-full border-collapse overflow-hidden rounded-xl border border-theme-subtle bg-theme-surface text-sm">
                <tbody>
                  {data.capital.map((l) => (
                    <tr key={l.id} className="hover:bg-theme-overlay/30">
                      <td className={LBL}>
                        <span className="text-theme-primary">
                          {l.committee} — {l.expense_type}
                        </span>
                        {l.supplier_detail && (
                          <span className="ml-2 text-[11px] text-theme-muted">
                            {l.supplier_detail}
                          </span>
                        )}
                        <Why text={l.reasoning} />
                      </td>
                      <td className={`${NUM} font-semibold text-theme-primary`}>
                        {usd0(l.fy27_v2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* OCA rollup — for the July-1 internal transfers (the 06-JUL
              committee-owner sign-offs, not a board vote) */}
          <div className="mt-6">
            <button
              onClick={() => setShowOca((s) => !s)}
              className="text-base font-semibold text-dcvfd-accent"
            >
              {showOca ? "▾" : "▸"} OCA rollup (for the July-1 internal
              transfers)
            </button>
            {showOca && (
              <table className="mt-2 w-full border-collapse overflow-hidden rounded-xl border border-theme-subtle bg-theme-surface text-sm">
                <tbody>
                  {data.oca_rollup.map((o) => (
                    <tr key={o.oca} className="hover:bg-theme-overlay/30">
                      <td className={LBL}>{o.oca || "(unmapped)"}</td>
                      <td className={NUM}>{usd0(o.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p className="mt-2 text-xs text-theme-muted">
              Once the county loads FY27 balances (~July 1), diff loaded-OCA vs
              these targets to generate the transfer schedule for the July-1
              internal transfers.
            </p>
          </div>
        </>
      )}
    </PageShell>
  );
}
